import { Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { AppInsightsServiceInjector } from '@next/core-lib/app-insights';
import { NotificationEmptyComponent, SummaryCardAction, SummaryCardComponent } from '@next/core-lib/components';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { BreakpointServiceInjector } from '@next/core-lib/responsive';

import { MatButton } from '@angular/material/button';
import { MatDivider } from '@angular/material/divider';
import { MatIcon } from '@angular/material/icon';
import { MatMenuTrigger } from '@angular/material/menu';
import { EmptyWidgetComponent } from '@app/shared/components/empty-widget.component';
import { LoadingSpinnerComponent } from '@app/shared/components/loading-spinner.component/loading-spinner.component';
import { RecalculateButtonComponent } from '@app/shared/components/recalculate-button/recalculate-button.component';
import { DiseaseHeaderComponent } from '@app/shared/components/widget-components/header.component';
import { DiseaseDashboardWidgetTableComponent } from '@app/shared/components/widget-components/table.component';
import { DateService } from '@app/shared/services/date.service';
import { CropYearService } from '@app/shared/services/crop-year.service';
import { areDateRangesSameDay, getDefaultDiseaseWidgetSelectedDates } from '@app/shared/utils/date-range.utils';
import { defaultBlightDayRange } from 'src/app/blight-v2/shared/helpers/blight-helpers';
import { DownyMildewActionMenuComponent } from '../../shared/components/action-menu.component';
import { DownyMildewAIIdentifiers, downyMildewELearningUrl } from '../../shared/constants';
import { DownyMildewSignalStore } from '../../signal-store/downy-mildew-signal-store';
import { CropFieldAdvice } from '@app/shared/components/disease-overview-table.component/models/models';

@Component({
  selector: 'app-downy-mildew-dashboard-widget',
  templateUrl: './downy-mildew-dashboard-widget.component.html',
  styleUrls: ['./downy-mildew-dashboard-widget.component.scss'],
  imports: [
    SummaryCardComponent,
    DiseaseHeaderComponent,
    MatDivider,
    MatButton,
    MatMenuTrigger,
    DownyMildewActionMenuComponent,
    MatIcon,
    RecalculateButtonComponent,
    DiseaseDashboardWidgetTableComponent,
    LoadingSpinnerComponent,
    NotificationEmptyComponent,
    EmptyWidgetComponent,
    TranslatePipe,
  ],
})
export class DownyMildewDashboardWidgetComponent {
  private readonly cropYearService = inject(CropYearService);
  protected readonly isCropYearReadOnly = this.cropYearService.isReadOnly;
  protected readonly store = inject(DownyMildewSignalStore);
  protected readonly translationPrefix = 'downyMildew';
  protected actions = [
    {
      id: 'info',
      icon: 'info_outline',
      callback: () => window.open(downyMildewELearningUrl),
    },
  ] satisfies SummaryCardAction[];
  protected readonly selectedCropFieldAdvices = signal<CropFieldAdvice[]>([]);
  protected readonly hasDownyMildewAdvices = computed(() => this.store.downyMildewAdvices().length > 0);
  protected readonly maximalDate = computed(() =>
    this.dateService.daysInTheFuture(defaultBlightDayRange.daysInFuture, this.store.cropYear()),
  );
  protected readonly isRecalculationDisabled = computed(
    () =>
      this.store.isDownyMildewCropFieldsSyncing() ||
      !this.hasDownyMildewAdvices() ||
      !this.store.hasEligibleDownyMildewCropFields(),
  );
  protected readonly recalculationLabelKey = computed(() =>
    this.store.isDownyMildewCropFieldsSyncing()
      ? 'downyMildew.shared.recalculating'
      : 'downyMildew.shared.recalculateAdviceAll',
  );
  private readonly localeService = inject(LocaleService);
  private readonly router = inject(Router);
  private readonly breakpointService = inject(BreakpointServiceInjector);
  private readonly ai = inject(AppInsightsServiceInjector);
  private readonly dateService = inject(DateService);
  protected readonly selectedDates = signal<Date[]>(
    getDefaultDiseaseWidgetSelectedDates(this.dateService, this.store.cropYear()),
  );
  private readonly ltMd = toSignal(this.breakpointService.ltMd$, { initialValue: false });
  protected readonly showAdviceButton = computed(() => !this.ltMd());
  private readonly amountOfSelectedCropFields = computed(() => this.selectedCropFieldAdvices().length);
  protected readonly isActionButtonDisabled = computed(() => this.amountOfSelectedCropFields() === 0);
  protected readonly actionButtonLabel = computed(() => {
    const count = this.amountOfSelectedCropFields();
    return count > 0
      ? this.localeService.translate('downyMildew.dashboardWidget.addAction', { count: count.toString() })
      : this.localeService.translate('downyMildew.dashboardWidget.addActionDisabled');
  });

  protected async onViewAdvice(): Promise<void> {
    this.ai.trackEvent(DownyMildewAIIdentifiers.viewAdvice, {});
    await this.router.navigate(['/downy-mildew']);
  }

  protected onSelectedDatesChange(dates: Date[]): void {
    if (dates.length < 2 || areDateRangesSameDay(this.dateService, dates, this.selectedDates())) {
      return;
    }

    this.store.patchSelectedDates(dates);
    this.selectedDates.set(dates);
  }

  protected onRecalculate(): void {
    this.ai.trackEvent(DownyMildewAIIdentifiers.recalc, {});
    this.store.recalculateDownyMildew(this.store.eligibleDownyMildewCropFieldIds());
  }

  protected onCropFieldSelectionChange(advices: CropFieldAdvice[]): void {
    this.selectedCropFieldAdvices.set(advices);
  }

  protected onAction(): void {
    this.ai.trackEvent(DownyMildewAIIdentifiers.addActionMyArable, {});
  }
}
